<template>
  <component-wrapper>
    <grid-container-fi
      :class="{
        'gallery-slider-fi': true,
        'gallery-slider-fi--exp': isEditMode,
        'gallery-slider-fi--ssr': !isMounted,
      }"
    >
      <div
        v-if="isEditMode"
        class="gallery-slider-fi__exp-info"
      >
        <icon-fi icon="alert-triangle" /> COMPONENT ONLY EDITABLE IN CONTENT EDITOR
      </div>

      <div class="fi-col-span-full">
        <div class="gallery-slider-fi__header">
          <richtext-fi
            class="gallery-slider-fi__text"
            :html-content="fields.text"
          />
          <template v-if="galleryItems.length > 2 || (galleryItems.length === 2 && !isMD)">
            <div
              class="gallery-slider-fi__arrow"
              @click="slider.prev()"
            >
              <arrow-icon class="backwards" />
            </div>
            <div
              class="gallery-slider-fi__arrow"
              @click="slider.next()"
            >
              <arrow-icon />
            </div>
          </template>
        </div>

        <slider-fi
          v-if="galleryItems.length"
          ref="slider"
          class="gallery-slider-fi__slider"
          :clipped-right="true"
          :settings="sliderSettings"
          @before-change="beforeSlideChanged"
          @after-change="afterSlideChanged"
        >
          <template
            v-for="(item, index) in galleryItems"
            :key="index"
            #[index]
          >
            <div class="gallery-slider-fi__item">
              <div
                v-if="isEditMode"
                class="gallery-slider-fi__item__editable"
              >
                <div
                  v-if="isVideo(item.fields)"
                  class="video-player-fi__overlay"
                >
                  <div class="video-player-fi__play-button">
                    <icon-fi icon="play" />
                  </div>
                </div>

                <div
                  v-if="item.fields.image?.value"
                  class="gallery-slider-fi__item__editable__image"
                >
                  <img :src="item.fields.image.value.src" />
                </div>
              </div>
              <div
                v-else
                class="gallery-slider-fi__item__image"
              >
                <ssr-cloud-image-fi
                  v-if="!isVideo(item.fields)"
                  :src="item.fields.image?.value?.src"
                  :alt="item.fields.image?.value?.alt"
                  :title="item.fields.image?.value?.title"
                  :aspect-ratio="16 / 9"
                  :unlimited="724"
                  :breakpoints="breakpoints"
                />

                <video-player-fi
                  v-else
                  :fields="item.fields"
                  :pause-video="slideIsChanging"
                  :aspect-ratio="16 / 9"
                  :unlimited="724"
                  :overlay-breakpoints="breakpoints"
                />
              </div>
              <div class="gallery-slider-fi__item__text">
                <!-- eslint-disable vue/no-v-html -->
                <p
                  v-if="item.fields?.headline?.value"
                  class="gallery-slider-fi__item__headline"
                  v-html="item.fields.headline.value"
                />
                <p
                  v-if="item.fields?.text?.value"
                  class="gallery-slider-fi__item__text"
                  v-html="item.fields.text.value"
                />
                <!-- eslint-enable vue/no-v-html -->
              </div>
            </div>
          </template>
        </slider-fi>

        <div
          v-if="isEditMode && !galleryItems.length"
          class="fi-my-42 fi-flex fi-items-center"
        >
          <icon-fi icon="alert-triangle" /> no active gallery item
        </div>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { ArrowIcon, IconFi, RichtextFi, SliderFi, SsrCloudImageFi } from 'atoms/index';
import type { Breakpoint } from 'atoms/ssr-cloud-image-fi/SsrCloudImageFi.vue';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from 'components/grid-fi/GridContainerFi.vue';
import VideoPlayerFi from 'components/video-player-fi/VideoPlayerFi.vue';
import { useBreakpoints } from 'composables/breakpoint';
import { useVideo } from 'composables/video';
import screenWidths from 'tailwind/screenWidths';
import { computed, onMounted, ref } from 'vue';

import type { SingleLineTextField } from '@/sitecoreFieldTypes';

const { isVideo } = useVideo();
const { isMD } = useBreakpoints();

defineOptions({
  name: 'GallerySliderFi',
});

const props = withDefaults(
  defineProps<{
    fields?: {
      galleryItems?: Array<any>;
      text?: SingleLineTextField;
    };
  }>(),
  {
    fields: () => ({}),
  },
);

const slider = ref<InstanceType<typeof SliderFi> | null>(null);

const slideIsChanging = ref(false);

const breakpoints: Array<Breakpoint> = [
  {
    mediaWidth: 366,
    imageWidth: 480,
  },
  {
    mediaWidth: 1023,
    imageWidth: 782,
  },
  {
    mediaWidth: 1439,
    imageWidth: 532,
  },
  {
    mediaWidth: 1440,
    imageWidth: 724,
  },
];

const isMounted = ref<boolean>(false);
onMounted(() => {
  isMounted.value = true;
});

const galleryItems = computed<Array<any>>(() => props.fields.galleryItems || []);

const sliderSettings: any = {
  dots: false,
  arrows: true,
  swipeToSlide: true,
  autoplay: false,
  autoplaySpeed: 9000,
  adaptiveHeight: false,
  slidesToShow: 2,
  slidesToScroll: 1,
  speed: 1000,
  mobileFirst: true,
  infinite: true,
  responsive: [
    {
      breakpoint: screenWidths.sm,
      settings: {
        arrows: false,
        slidesToShow: 1,
      },
    },
  ],
};

const beforeSlideChanged = (): void => {
  slideIsChanging.value = true;
};

const afterSlideChanged = (): void => {
  slideIsChanging.value = false;
};
</script>

<style scoped lang="scss">
@import './gallery-slider-fi';
</style>
